import { LEAGUE } from "../models/NBA";
import { VideoEvent } from "../client";

export const isValidPlayer = (id?: string | number | null, name?: string | null): boolean => {
  const cleanId = id !== null && id !== undefined ? String(id).trim() : "";
  const cleanName = name !== null && name !== undefined ? String(name).trim() : "";
  const hasValidId = cleanId !== "" && cleanId !== "0" && cleanId !== "None" && cleanId !== "null" && cleanId !== "undefined";
  const hasValidName = cleanName !== "" && cleanName !== "None" && cleanName !== "null" && cleanName !== "undefined";
  return hasValidId && hasValidName;
};

export const getPlayerHeadshotUrl = (
  playerId?: string | number | null,
  league: LEAGUE | string = LEAGUE.NBA
): string | undefined => {
  if (!playerId) return undefined;
  const cleanId = String(playerId).trim();
  if (cleanId === "" || cleanId === "0" || cleanId === "None" || cleanId === "null" || cleanId === "undefined") {
    return undefined;
  }
  const leagueSlug = (league || LEAGUE.NBA).toLowerCase();
  return `https://cdn.${leagueSlug}.com/headshots/${leagueSlug}/latest/260x190/${cleanId}.png`;
};

export const getPlayerInitials = (name?: string | null): string => {
  if (!name) return "";
  const clean = name.trim();
  if (clean === "" || clean === "None" || clean === "null" || clean === "undefined") return "";
  const parts = clean.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export interface EventPlayerInfo {
  id?: string | null;
  name?: string | null;
}

export interface EventPlayers {
  primary: EventPlayerInfo;
  secondary?: EventPlayerInfo;
}

export const getTargetPlayerSlot = (
  video: VideoEvent,
  targetPlayerId?: string | number | null,
  targetPlayerName?: string | null
): 1 | 2 | 3 | null => {
  const cleanTargetId =
    targetPlayerId !== null && targetPlayerId !== undefined ? String(targetPlayerId).trim() : null;
  const hasTargetId =
    cleanTargetId !== null &&
    cleanTargetId !== "" &&
    cleanTargetId !== "0" &&
    cleanTargetId !== "None" &&
    cleanTargetId !== "null" &&
    cleanTargetId !== "undefined";

  const cleanTargetName = targetPlayerName ? targetPlayerName.trim().toLowerCase() : null;
  const lastName = cleanTargetName ? cleanTargetName.split(/\s+/).pop() : null;
  const desc = video.desc ? video.desc.toLowerCase() : "";

  if (hasTargetId) {
    if (video.player3Id && String(video.player3Id).trim() === cleanTargetId) {
      return 3;
    }
    if (video.player2Id && String(video.player2Id).trim() === cleanTargetId) {
      if (desc.includes("block") || desc.includes("blk")) {
        return 3;
      }
      return 2;
    }
    if (video.playerId && String(video.playerId).trim() === cleanTargetId) {
      const p1Name = video.playerName ? video.playerName.trim().toLowerCase() : "";
      const p1Last = p1Name ? p1Name.split(/\s+/).pop() : "";
      const isBlockDesc =
        (desc.includes("block") || desc.includes("blk")) &&
        ((p1Last && p1Last.length >= 3 && (desc.includes(p1Last + " block") || desc.includes("block by " + p1Last) || desc.includes(p1Last + " blk") || desc.startsWith(p1Last))) ||
         (lastName && lastName.length >= 3 && (desc.includes(lastName + " block") || desc.includes("block by " + lastName) || desc.includes(lastName + " blk") || desc.startsWith(lastName))));
      if (isBlockDesc) {
        return 3;
      }
      return 1;
    }
  }

  if (cleanTargetName) {
    if (video.player3Name && video.player3Name.trim().toLowerCase() === cleanTargetName) return 3;
    if (video.player2Name && video.player2Name.trim().toLowerCase() === cleanTargetName) {
      if (desc.includes("block") || desc.includes("blk")) return 3;
      return 2;
    }
    if (video.playerName && video.playerName.trim().toLowerCase() === cleanTargetName) {
      if (desc.includes("block") || desc.includes("blk")) return 3;
      return 1;
    }
  }

  if (lastName && lastName.length >= 3) {
    if (video.player3Name && video.player3Name.trim().toLowerCase().includes(lastName)) return 3;
    if (video.player2Name && video.player2Name.trim().toLowerCase().includes(lastName)) {
      if (desc.includes("block") || desc.includes("blk")) return 3;
      return 2;
    }
    if (video.playerName && video.playerName.trim().toLowerCase().includes(lastName)) {
      if (desc.includes(lastName + " block") || desc.includes("block by " + lastName) || desc.includes(lastName + " blk")) return 3;
      return 1;
    }

    if (desc.includes(lastName) && (desc.includes("block") || desc.includes("blk"))) {
      return 3;
    }
    if (desc.includes(lastName) && desc.includes(" ast)")) {
      return 2;
    }
    if (desc.includes(lastName) && (desc.includes("steal") || desc.includes("stl"))) {
      return 2;
    }
  }

  return null;
};

export const getEventPlayers = (
  video: VideoEvent,
  targetPlayerId?: string | null,
  targetPlayerName?: string | null
): EventPlayers => {
  const isP1Valid = isValidPlayer(video.playerId, video.playerName);
  const isP2Valid = isValidPlayer(video.player2Id, video.player2Name);

  const slot = getTargetPlayerSlot(video, targetPlayerId, targetPlayerName);

  if (slot === 2) {
    return {
      primary: isP2Valid
        ? { id: video.player2Id, name: video.player2Name }
        : { id: targetPlayerId, name: targetPlayerName || video.player2Name || "Player" },
      secondary: isP1Valid ? { id: video.playerId, name: video.playerName } : undefined,
    };
  }

  if (slot === 3) {
    const p3Name =
      (video.player3Name && video.player3Name !== "None" ? video.player3Name : null) ||
      targetPlayerName ||
      "Player";
    return {
      primary: { id: video.player3Id || targetPlayerId, name: p3Name },
      secondary: isP1Valid ? { id: video.playerId, name: video.playerName } : undefined,
    };
  }

  if (slot === 1) {
    return {
      primary: { id: video.playerId, name: video.playerName },
      secondary: isP2Valid ? { id: video.player2Id, name: video.player2Name } : undefined,
    };
  }

  if (targetPlayerId) {
    const cleanTarget = String(targetPlayerId).trim();
    return {
      primary: { id: targetPlayerId, name: targetPlayerName || video.playerName },
      secondary: isP1Valid && String(video.playerId).trim() !== cleanTarget
        ? { id: video.playerId, name: video.playerName }
        : undefined,
    };
  }

  return {
    primary: { id: video.playerId, name: video.playerName },
    secondary: isP2Valid ? { id: video.player2Id, name: video.player2Name } : undefined,
  };
};

export const resolveTargetPlayerId = (
  video: VideoEvent,
  targetPlayerIds?: string[],
  stats?: string[]
): string | undefined => {
  if (!targetPlayerIds || targetPlayerIds.length === 0) return undefined;

  const p1Match = video.playerId && targetPlayerIds.includes(video.playerId) ? video.playerId : null;
  const p2Match = video.player2Id && targetPlayerIds.includes(video.player2Id) ? video.player2Id : null;
  const p3Match = video.player3Id && targetPlayerIds.includes(video.player3Id) ? video.player3Id : null;

  // Single match cases
  if (p2Match && !p1Match && !p3Match) return p2Match;
  if (p3Match && !p1Match && !p2Match) return p3Match;
  if (p1Match && !p2Match && !p3Match) return p1Match;

  // Multiple matched players in the same play:
  const desc = video.desc ? video.desc.toLowerCase() : "";
  const statsList = (stats || []).map((s) => s.toUpperCase());

  // 1. Block: if player3 (blocker) is matched
  if (p3Match && (desc.includes("block") || desc.includes("blk"))) {
    return p3Match;
  }

  // 2. Steal: if player2 (stealer) is matched
  if (p2Match && (desc.includes("steal") || desc.includes("stl"))) {
    return p2Match;
  }

  // 3. Assist vs Scorer:
  if (p2Match && p1Match && (desc.includes(" ast)") || desc.includes("assist"))) {
    if (statsList.includes("AST") && !statsList.some((s) => ["PTS", "3PT", "FG3M", "FGM", "FGA", "FG3A"].includes(s))) {
      return p2Match;
    }
    return p1Match;
  }

  // 4. Default priority: p2Match > p3Match > p1Match
  return p2Match || p3Match || p1Match || targetPlayerIds[0];
};
